import { useMemo, useState } from 'react';
import { Button, Box, InputLabel } from '@mui/material';
import { useTranslate, useRecordContext } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { GrayDateInputWithUpperLabel } from './GrayDateInputWithUpperLabel';

type PlannedRenewalDateInputFieldProps = {
  label?: string;
  clearButtonPlacement?: 'inline' | 'below';
  fullWidth?: boolean;
  size?: 'small' | 'medium';
};

const ClearButton = (props: { onClick: () => void; tabIndex?: number }) => {

  const { onClick, tabIndex } = props;
  const translate = useTranslate();
  return (
    <Button sx={{
      p: 0,
      m: 0,
      minWidth: 0,
      minHeight: 0,
      textTransform: 'none',
      textDecoration: 'underline',
      justifyContent: 'flex-start',
      fontWeight: 'normal',
      fontSize: 'inherit',
      lineHeight: 'inherit',
      '&:hover': {
        textDecoration: 'underline',
        backgroundColor: 'transparent',
      },
    }} onClick={onClick} tabIndex={tabIndex}>
      {translate('general.text.clear')}
    </Button>
  );

};

export const PlannedRenewalDateInputField = (props: PlannedRenewalDateInputFieldProps) => {

  const {
    label = 'resources.security-inventory-items.fields.planned_inspection_date',
    clearButtonPlacement = 'inline',
    fullWidth = false,
    size = 'medium',
  } = props;

  const translate = useTranslate();
  const record = useRecordContext();
  const { setValue } = useFormContext();

  const [addPlannedRenewalDateClicked, setAddPlannedRenewalDateClicked] = useState(false);
  const [removedPlannedRenewalDate, setRemovedPlannedRenewalDate] = useState(false);
  const showPlannedRenewalDateField = useMemo(() => {

    return (record && (record.plannedRenewalDate || addPlannedRenewalDateClicked)) && !removedPlannedRenewalDate;

  }, [record, addPlannedRenewalDateClicked, removedPlannedRenewalDate]);

  const clearPlannedRenewalDate = () => {

    setValue('plannedRenewalDate', null);
    setRemovedPlannedRenewalDate(true);

  };

  return (
    <Box sx={{
      position: clearButtonPlacement === 'inline' ? 'relative' : undefined,
      maxWidth: fullWidth ? undefined : 160,
      width: fullWidth ? '100%' : undefined,
      display: clearButtonPlacement === 'below' ? 'flex' : undefined,
      flexDirection: clearButtonPlacement === 'below' ? 'column' : undefined,
      alignItems: clearButtonPlacement === 'below' ? 'flex-start' : undefined,
    }}>
      {showPlannedRenewalDateField ? (
        <GrayDateInputWithUpperLabel
          source="plannedRenewalDate"
          label={label}
          helperText={false}
          size={size}
          sx={{ width: fullWidth ? '100%' : 160, mt: 0, mb: 0 }}
        />
      ) : (
        <>
          <InputLabel className="input-label">
            {translate(label)}
          </InputLabel>
          <Box
            role="button"
            tabIndex={0}
            aria-label={translate('resources.security-inventory-items.text.not_planned_yet')}
            onClick={() => {

              setAddPlannedRenewalDateClicked(true);
              setRemovedPlannedRenewalDate(false);

            }}
            sx={{
              width: fullWidth ? '100%' : 160,
              height: size === 'small' ? 40 : 56,
              backgroundColor: '#f1f1f1',
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              px: 1.5,
              color: 'text.secondary',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
          >
            {translate('resources.security-inventory-items.text.not_planned_yet')}
          </Box>
        </>
      )}
      {clearButtonPlacement === 'below' && (
        <Box
          sx={{
            mt: 0.5,
            minHeight: '2.4em',
            visibility: showPlannedRenewalDateField ? 'visible' : 'hidden',
          }}
          aria-hidden={!showPlannedRenewalDateField}
        >
          <ClearButton
            onClick={clearPlannedRenewalDate}
            tabIndex={showPlannedRenewalDateField ? 0 : -1}
          />
        </Box>
      )}
      {clearButtonPlacement === 'inline' && showPlannedRenewalDateField && (
        <Box sx={{
          position: 'absolute',
          left: '100%',
          bottom: 0,
          ml: 1,
          height: size === 'small' ? 40 : 56,
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
        }}>
          <ClearButton onClick={clearPlannedRenewalDate} />
        </Box>
      )}
    </Box>
  );

};
